import { defineVariants } from '@/runtime/style-engine/define-variants';

/**
 * Tailwind-like class variants for `<e-card>`.
 */
export const cardClasses = defineVariants({
  base: `
    w-full
    overflow-hidden
  `,
  variants: {
    color: {
      primary: `[--card-color:var(--color-primary)] [--card-color-alt:var(--color-primary-elevation-600)]`,
      secondary: `[--card-color:var(--color-secondary)] [--card-color-alt:var(--color-secondary-elevation-600)]`,
      accent: `[--card-color:var(--color-accent)] [--card-color-alt:var(--color-accent-elevation-600)]`,
      error: `[--card-color:var(--color-error)] [--card-color-alt:var(--color-error-elevation-600)]`,
      warning: `[--card-color:var(--color-warning)] [--card-color-alt:var(--color-warning-elevation-600)]`,
      info: `[--card-color:var(--color-info)] [--card-color-alt:var(--color-info-elevation-600)]`,
      success: `[--card-color:var(--color-success)] [--card-color-alt:var(--color-success-elevation-600)]`,
      neutral: `[--card-color:var(--color-neutral)] [--card-color-alt:var(--color-elevation-600)] !text-(--color-elevation-950)`,
      mono: `[--card-color:var(--color-elevation-950)] [--card-color-alt:var(--color-elevation-950)] !text-(--color-elevation-50)`
    },

    variant: {
      solid: `bg-(--card-color) text-white`,
      outline: `ring ring-(--card-color-alt) text-(--card-color-alt)`,
      soft: `bg-(--card-color)/15 text-(--card-color-alt)`,
      subtle: `ring ring-(--card-color)/35 bg-(--card-color)/15 text-(--card-color)`
    },

    size: {
      xs: `p-2 rounded-lg`,
      sm: `p-3 rounded-lg`,
      md: `p-4 rounded-xl`,
      lg: `p-5 rounded-xl`,
      xl: `p-6 rounded-xl`
    }
  },

  defaults: {
    color: 'neutral',
    variant: 'subtle',
    size: 'md'
  },

  compoundVariants: [
    {
      color: 'neutral',
      variant: 'solid',
      class: `![--card-color:var(--color-card)]`
    },
    {
      size: ['xs', 'sm'],
      variant: ['outline', 'subtle'],
      class: 'ring-1'
    },
    {
      size: ['md', 'lg', 'xl'],
      variant: ['outline', 'subtle'],
      class: 'ring-2'
    }
  ]
});

/**
 * Tailwind-like class variants for `<e-card-header>`.
 */
export const cardHeaderClasses = defineVariants({
  base: `w-full`,
  variants: {}
});

/**
 * Tailwind-like class variants for `<e-card-header>` > heading.
 */
export const cardHeaderHeadingClasses = defineVariants({
  base: ``,
  variants: {
    color: {
      primary: `text-(--color-primary-elevation-600)`,
      secondary: `text-(--color-secondary-elevation-600)`,
      accent: `text-(--color-accent-elevation-600)`,
      error: `text-(--color-error-elevation-600)`,
      warning: `text-(--color-warning-elevation-600)`,
      info: `text-(--color-info-elevation-600)`,
      success: `text-(--color-success-elevation-600)`,
      neutral: ``,
      mono: ``
    },
    variant: {
      solid: ``,
      outline: ``,
      soft: ``,
      subtle: ``
    }
  },
  defaults: {
    color: 'neutral',
    variant: 'subtle'
  },
  compoundVariants: [
    // Solid variant: white text for all except neutral
    {
      variant: 'solid',
      color: ['*', '!neutral'],
      class: '!text-white'
    },
    // Solid + neutral: black text
    {
      variant: 'solid',
      color: 'neutral',
      class: '!text-(--color-elevation-950)'
    }
  ]
});

/**
 * Tailwind-like class variants for `<e-card-header>` > description.
 */
export const cardHeaderDescriptionClasses = defineVariants({
  base: ``,
  variants: {
    color: {
      primary: `text-(--color-primary-elevation-600)/60`,
      secondary: `text-(--color-secondary-elevation-600)/60`,
      accent: `text-(--color-accent-elevation-600)/60`,
      error: `text-(--color-error-elevation-600)/60`,
      warning: `text-(--color-warning-elevation-600)/60`,
      info: `text-(--color-info-elevation-600)/60`,
      success: `text-(--color-success-elevation-600)/60`,
      neutral: `text-(--color-foreground)/60`,
      mono: `text-white/70`
    },
    variant: {
      solid: ``,
      outline: ``,
      soft: ``,
      subtle: ``
    }
  },
  defaults: {
    color: 'neutral',
    variant: 'subtle'
  },
  compoundVariants: [
    // Solid variant: white/70 text for all except neutral
    {
      variant: 'solid',
      color: ['*', '!neutral'],
      class: '!text-white/70'
    },
    // Solid + neutral: black/60 text
    {
      variant: 'solid',
      color: 'neutral',
      class: '!text-(--color-elevation-950)/60'
    }
  ]
});
